import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import KinoraSidebar from './components/KinoraSidebar';
import MobileTabBar from './components/MobileTabBar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import MarketingHeader from './components/MarketingHeader';
import Home from './pages/Home';
import Landing from './pages/Landing';
import Browse from './pages/Browse';
import Video from './pages/Video';
import ContentDetail from './pages/ContentDetail';
import Crowdfunding from './pages/Crowdfunding';
import CommunityFund from './pages/CommunityFund';
import About from './pages/About';
import Guild from './pages/Guild';
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import Watchlist from './pages/Watchlist';
import WatchHistory from './pages/WatchHistory';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import StudioPage from './pages/StudioPage';
import Creator from './pages/Creator';
import NotFound from './pages/NotFound';
import Pricing from './pages/Pricing';
import SliceHistory from './pages/SliceHistory';
import Notifications from './pages/Notifications';

// Routes that keep the marketing layout (no Kinora sidebar shell).
const MARKETING_ROUTES = new Set(['/', '/auth', '/about', '/terms', '/privacy', '/pricing', '/guild']);

function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const useSidebar = !MARKETING_ROUTES.has(location.pathname);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  if (useSidebar) {
    return (
      <div className='bg-background font-sans text-foreground min-h-screen flex'>
        <KinoraSidebar />
        <div className='flex-1 min-w-0 flex flex-col pb-16 lg:pb-0'>
          <main className='flex-1 min-w-0'>{children}</main>
          <Footer />
        </div>
        <MobileTabBar />
      </div>
    );
  }

  return (
    <div className='bg-background font-sans text-foreground min-h-screen'>
      <MarketingHeader />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
          <AppLayout>
            <Routes>
              <Route path='/' element={<Landing />} />
              <Route path='/home' element={<Home />} />
              <Route path='/browse' element={<Browse />} />
              <Route path='/watch/:slug' element={<ContentDetail />} />
              <Route
                path='/video/:id'
                element={
                  <ProtectedRoute>
                    <Video />
                  </ProtectedRoute>
                }
              />
              <Route path='/crowdfund' element={<Crowdfunding />} />
              <Route path='/crowdfund/:id' element={<Crowdfunding />} />
              <Route path='/fund' element={<CommunityFund />} />
              <Route path='/studio/:slug' element={<StudioPage />} />
              <Route path='/creator/:id' element={<Creator />} />
              <Route path='/about' element={<About />} />
              <Route path='/guild' element={<Guild />} />
              <Route path='/terms' element={<Terms />} />
              <Route path='/privacy' element={<Privacy />} />
              <Route
                path='/profile'
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/watchlist'
                element={
                  <ProtectedRoute>
                    <Watchlist />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/history'
                element={
                  <ProtectedRoute>
                    <WatchHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/auth'
                element={
                  <ProtectedRoute requireAuth={false}>
                    <Auth />
                  </ProtectedRoute>
                }
              />
              <Route path='/pricing' element={<Pricing />} />
              <Route path='/reset-password' element={<ResetPassword />} />
              <Route
                path='/slices'
                element={
                  <ProtectedRoute>
                    <SliceHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/notifications'
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route path='*' element={<NotFound />} />
            </Routes>
          </AppLayout>
          <Toaster />
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App;
